import * as pdfjs from "pdfjs-dist/build/pdf.mjs";
import "pdfjs-dist/build/pdf.worker.mjs";

class CommonUtils {

    dispatchEvent(name, payload) {
        const event = new Event(name);
        event.payload = payload;
        document.dispatchEvent(event);
    }

    debounce(timeout, func) {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                func.apply(this, args);
            }, timeout);
        };
    }

    throttle(func, limit) {
        let lastCall = 0;
        return (...args) => {
            const now = Date.now();
            if (now - lastCall >= limit) {
                lastCall = now;
                func(...args);
            }
        };
    }

    parseDate(string) {
        if (this.isBlank(string)) {
            return null;
        }
        const clear = string.replace(/[^\x00-\x7F]/g, ""); //HACK: ie workaround
        const [day, month, year] = clear.split(".");
        if (!day || !month || !year || year.length < 4) {
            return null;
        }
        const date = new Date(Number(year), Number(month) - 1, Number(day));
        if (isNaN(date)) {
            return null;
        }
        return date;
    }

    isBlank(value) {
        return (value === null || value === undefined || /^\s*$/.test(value));
    }

    hasRadioWithoutOperator(entry) {
        return entry.sectors?.some(sector => !sector.operator
            && sector.radios?.some(radio => radio.enabled && !radio.antennaVirtual)) ?? false;
    }

    detectBrowser() {
        /* eslint-disable */
        const isOpera = (!!window.opr && !!opr.addons) || !!window.opera || navigator.userAgent.indexOf(" OPR/") >= 0;
        //firefox 1.0+
        const isFirefox = typeof InstallTrigger !== "undefined";
        //safari 3.0+
        const isSafari = /constructor/i.test(window.HTMLElement)
            || (function (p) { return p.toString() === "[object SafariRemoteNotification]"; })
                (!window["safari"] || (typeof safari !== "undefined" && safari.pushNotification));
        //ie 6-11
        const isIE = /*@cc_on!@*/false || !!document.documentMode;
        //edge 20+
        const isEdge = !isIE && !!window.StyleMedia;
        //chrome 1+
        const isChrome = window.chrome;
        /* eslint-enable */

        if (isOpera) {
            return "opera";
        } else if (isFirefox) {
            return "firefox";
        } else if (isSafari) {
            return "safari";
        } else if (isIE) {
            return "ie";
        } else if (isEdge) {
            return "edge";
        } else if (isChrome) {
            return "chrome";
        }
        return null;
    }

    download(url, filename) {
        return new Promise(resolve => {
            const anchor = document.createElement("a");
            anchor.setAttribute("href", url);
            anchor.setAttribute("download", filename ?? "");
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            resolve("");
        });
    }

    print(promise) {
        const browser = this.detectBrowser();
        if (browser === "firefox" || browser === "edge") {
            //first open blank window then load url to prevent popup blocking
            const win = window.open("", "_blank");
            return promise.then(url => (win.location.href = url));
        }
        //chrome, safari, ie
        return promise.then(url => {
            const iframe = window.document.createElement("iframe");
            iframe.src = url;
            iframe.width = "0";
            iframe.height = "0";
            iframe.style.cssText = "display: block; display: hidden;";
            window.document.body.appendChild(iframe);
        });
    }

    readFile(event) {
        const filelist = event.target.files;
        return new Promise((resolve, reject) => {
            for (const file of filelist) {
                const reader = new FileReader();
                reader.name = file.name;
                reader.onload = event => {
                    try {
                        resolve(event.currentTarget);
                    } catch (e) {
                        reject(e);
                    }
                };
                reader.onerror = () => {
                    reject(new Error("Error reading the file."));
                };
                if (filelist.length > 0) {
                    reader.readAsArrayBuffer(file);
                } else {
                    reject(new Error("No files selected."));
                }
            }
        });
    }

    async loadPdfFromFile(file) {
        const url = URL.createObjectURL(file);
        const pdf = await pdfjs.getDocument({ url }).promise;
        return pdf;
    }

    async loadPdfFromUrl(url) {
        const pdf = await pdfjs.getDocument({ url }).promise;
        return pdf;
    }

    // Crops a png data url to a region given as fractions (0..1) of the image size, so the
    // same region can be applied to renderings of different scales (PlanWizard preview/final).
    async cropPng(imageSrc, region) {
        const image = await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error("Error loading the image."));
            img.src = imageSrc;
        });
        const clamp = (value, max) => Math.min(Math.max(value, 0), max);
        const left = clamp(Math.round(region.x * image.width), image.width - 1);
        const top = clamp(Math.round(region.y * image.height), image.height - 1);
        const width = clamp(Math.round(region.width * image.width), image.width - left);
        const height = clamp(Math.round(region.height * image.height), image.height - top);
        if (width < 1 || height < 1) {
            return imageSrc;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext("2d");
        context.drawImage(image, left, top, width, height, 0, 0, width, height);
        return canvas.toDataURL("image/png");
    }

    // Converts a png data url to grayscale using the standard luminance weights, so a plan
    // can be imported without color information (e.g. to make overlaid features stand out more).
    async grayscalePng(imageSrc) {
        const image = await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error("Error loading the image."));
            img.src = imageSrc;
        });
        const canvas = document.createElement("canvas");
        canvas.width = image.width;
        canvas.height = image.height;
        const context = canvas.getContext("2d");
        context.drawImage(image, 0, 0);
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        const { data } = imageData;
        for (let i = 0; i < data.length; i += 4) {
            const gray = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
            data[i] = gray;
            data[i + 1] = gray;
            data[i + 2] = gray;
        }
        context.putImageData(imageData, 0, 0);
        return canvas.toDataURL("image/png");
    }

    async pdfPageToPng(pdf, pageNum, scale) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const context = canvas.getContext("2d");
        const renderContext = {
            canvasContext: context,
            viewport: viewport
        };
        await page.render(renderContext).promise;

        const imageData = canvas.toDataURL("image/png");
        return imageData;
    }
}
const commonUtils = new CommonUtils();
export default commonUtils;